import * as React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { AppMenu, Flex, Text } from '@ghq-abi/design-system';

import { useNav } from '~/app/contexts/NavContext';
import { EEventsLabels } from '~/app/hocs/eventsAnalytics/eventsEnum';
import { useEvents } from '~/app/hocs/eventsAnalytics/useEvents';
import { BackArrowIcon } from '~/shared/components/icons';

import cheersMenuLogo from '~/../public/img/cheers_menu_logo.png';

import { useMenuListScroll } from '../../hooks/useMenuListScroll';

export function DesktopMenu() {
    const {
        isAppMenuOpen,
        subMenuOpen,
        menuHeaderItem,
        menuItems,
        toggleAppMenu,
        checkIfIsActive,
        toggleSubMenu,
        footerItems,
    } = useNav();

    const { elementRef, isScrollable } = useMenuListScroll();
    const { fireClickEvent } = useEvents();
    const handleHelpSubItemClick = React.useCallback((path?: string) => {
        fireClickEvent({
            event: EEventsLabels.SOMETHING_NOT_WORKING_CLICK,
            page: 'navigation',
            component: 'DesktopMenu',
            action: 'help_center_item_click',
            properties: {
                menuKey: 'isSomethingNotWorking',
            },
        });

        window.open(path, '_blank', 'noopener,noreferrer');
    }, [fireClickEvent]);

    return (
        <AppMenu open={isAppMenuOpen}>
            <AppMenu.Trigger asChild>
                <AppMenu.Bar openMenu={toggleAppMenu} openMenuDelay={300}>
                    <AppMenu.Header css={{ p: 0 }}>
                        <AppMenu.MenuItem
                            css={{ p: '$3 $2' }}
                            leftIcon={menuHeaderItem.icon}
                            isTriggerItem
                        />
                    </AppMenu.Header>

                    <AppMenu.MenuList>
                        {menuItems.map((item) => (
                            <AppMenu.MenuItem
                                key={item.key}
                                leftIcon={item.icon}
                                isActive={checkIfIsActive(item.path ?? '', item.routeComponents)}
                                isTriggerItem
                            />
                        ))}
                    </AppMenu.MenuList>

                    <AppMenu.Footer css={{ mt: 'auto' }}>
                        <AppMenu.MenuList>
                            {footerItems.map((item) => (
                                <AppMenu.MenuItem
                                    key={item.key}
                                    leftIcon={item.icon}
                                    onHover={() => toggleSubMenu(item)}
                                    onHoverDelay={300}
                                    onClick={() => toggleSubMenu(item)}
                                    isTriggerItem
                                    css={{
                                        py: 0,
                                    }}
                                />
                            ))}
                        </AppMenu.MenuList>
                    </AppMenu.Footer>
                </AppMenu.Bar>
            </AppMenu.Trigger>

            <AppMenu.Content
                closeMenuDelay={300}
                css={{ justifyContent: 'flex-start' }}
                closeMenu={() => {
                    toggleAppMenu();
                    toggleSubMenu(null);
                }}
            >
                <AppMenu.Header css={{ p: 0 }}>
                    <AppMenu.MenuItem
                        title={menuHeaderItem.title}
                        hasSubmenu
                        css={{ p: '$3 $2' }}
                        leftIcon={menuHeaderItem.icon}
                        isSubMenuOpen={subMenuOpen?.key === menuHeaderItem.key}
                        onClick={() => toggleSubMenu(menuHeaderItem)}
                        renderSubmenu={
                            <AppMenu.SubMenu
                                title={menuHeaderItem.title || ''}
                                alignWithHeader
                                hideTrigger
                                renderTitle={
                                    <Flex align="center" gap="md" css={{ height: '$full' }}>
                                        <AppMenu.MenuIconButton
                                            onClick={() => toggleSubMenu(null)}
                                            icon={<BackArrowIcon />}
                                        />
                                        <Text css={{ fontWeight: '$medium' }}>
                                            {menuHeaderItem.title}
                                        </Text>
                                    </Flex>
                                }
                            >
                                <AppMenu.MenuList>
                                    {menuHeaderItem.routeComponents?.map((subitem) => (
                                        <AppMenu.MenuItem
                                            key={subitem.key}
                                            title={subitem.title}
                                            route={subitem.path}
                                            leftIcon={subitem.icon}
                                            isSubMenuItem
                                        />
                                    ))}
                                </AppMenu.MenuList>
                            </AppMenu.SubMenu>
                        }
                    />
                </AppMenu.Header>

                <AppMenu.MenuList
                    isMainList
                    ref={elementRef}
                    bottomElement={
                        <Image
                            src={cheersMenuLogo}
                            placeholder="blur"
                            alt="Cheers Hub"
                            style={{ marginBottom: '16px' }}
                        />
                    }
                    bottomElementCss={{
                        width: isScrollable ? 'calc($appMenuOpenWidth - $2)' : '$appMenuOpenWidth',
                    }}
                >
                    {menuItems.map((item) => (
                        <AppMenu.MenuItem
                            key={item.key}
                            route={item.external ? undefined : item.path}
                            title={item.title}
                            leftIcon={item.icon}
                            as={item.external ? undefined : Link}
                            onClick={
                                item.external
                                    ? () => window.open(item.path, '_blank', 'noopener,noreferrer')
                                    : undefined
                            }
                            hasSubmenu={!!item.routeComponents?.length}
                            isSubMenuOpen={subMenuOpen?.key === item.key}
                            onHover={() => toggleSubMenu(item)}
                            onHoverDelay={300}
                            isActive={checkIfIsActive(item.path ?? '', item.routeComponents)}
                            renderSubmenu={
                                <AppMenu.SubMenu
                                    title={item.title || ''}
                                    css={{ borderRadius: '$4' }}
                                >
                                    <AppMenu.MenuList>
                                        {item.routeComponents?.map((subitem) => (
                                            <AppMenu.MenuItem
                                                key={subitem.key}
                                                title={subitem.title}
                                                route={subitem.path}
                                                as={Link}
                                                isSubMenuItem
                                            />
                                        ))}
                                    </AppMenu.MenuList>
                                </AppMenu.SubMenu>
                            }
                        />
                    ))}
                </AppMenu.MenuList>

                <AppMenu.Footer
                    css={{
                        borderTop: 'none',
                    }}
                >
                    {footerItems.map((item) => (
                        <AppMenu.MenuItem
                            key={item.key}
                            route={item.path}
                            title={item.title}
                            leftIcon={item.icon}
                            hasSubmenu={!!item.routeComponents?.length}
                            isSubMenuOpen={subMenuOpen?.key === item.key}
                            onHover={() => toggleSubMenu(item)}
                            onHoverDelay={300}
                            onClick={() => toggleSubMenu(item)}
                            isActive={checkIfIsActive(item.path ?? '', item.routeComponents)}
                            renderSubmenu={
                                item.key === 'help' ? (
                                    <AppMenu.SubMenu
                                        title={item.title}
                                        renderTitle={
                                            <Text
                                                css={{
                                                    fontWeight: '$medium',
                                                    fontSize: '$sm',
                                                }}
                                            >
                                                {item.title}
                                            </Text>
                                        }
                                    >
                                        <AppMenu.MenuList>
                                            {item.routeComponents?.map((subitem) => (
                                                <AppMenu.MenuItem
                                                    key={subitem.key}
                                                    title={subitem.title}
                                                    route={
                                                        subitem.external ? undefined : subitem.path
                                                    }
                                                    isSubMenuItem
                                                    onClick={
                                                        subitem.external
                                                            ? () =>
                                                                  handleHelpSubItemClick(
                                                                      subitem.path,
                                                                  )
                                                            : undefined
                                                    }
                                                />
                                            ))}
                                        </AppMenu.MenuList>
                                    </AppMenu.SubMenu>
                                ) : null
                            }
                        />
                    ))}
                </AppMenu.Footer>
            </AppMenu.Content>
        </AppMenu>
    );
}
